// src/features/dashboard/components/ManagerDashboard/ProductivityReport/ProductivityDistributionChart.tsx

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  Cell,
  Tooltip,
} from 'recharts';

import {
  ProductivityDistributionResponse,
} from '../../../types/Manager/productivityReport.types';

interface Props {
  data: ProductivityDistributionResponse[];
}

interface TooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const COLORS = [
  '#EB5757',
  '#F2994A',
  '#F68B38',
  '#EAA526',
  '#49C46E',
  '#42B061',
  '#40995B',
];

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  return (
    <div className="min-w-[150px] rounded-lg border border-[#D9DDE3] bg-white px-3 py-2 shadow-lg">
      <div className="mb-2 text-[11px] font-semibold text-[#1E293B]">
        {label}
      </div>

      <div className="flex items-center justify-between text-[11px]">
        <span className="text-[#64748B]">
          Associates
        </span>

        <span className="font-semibold text-[#003E7E]">
          {payload[0]?.value ?? 0}
        </span>
      </div>
    </div>
  );
};

const ProductivityDistributionChart = ({
  data,
}: Props) => {
  const chartData = data.map(
    (item, index) => ({
      range: item.productivityRange,
      value: Number(item.employeeCount) || 0,
      color:
        COLORS[index % COLORS.length],
    })
  );

  /*
   * Find the largest number of associates.
   *
   * Y-axis will always start from 0.
   * We also make sure the maximum has enough
   * space above the highest bar.
   */
  const maxValue = Math.max(
    ...chartData.map(
      (item) => item.value
    ),
    0
  );

  /*
   * Create a clean integer Y-axis maximum.
   *
   * Examples:
   *  0  -> 5
   *  1  -> 5
   *  4  -> 5
   *  6  -> 10
   *  11 -> 15
   *  23 -> 25
   */
  const getYAxisMax = (
    value: number
  ): number => {
    if (value <= 0) {
      return 5;
    }

    const step =
      value <= 5
        ? 1
        : value <= 20
        ? 5
        : 10;

    return (
      Math.ceil(value / step) *
        step +
      step
    );
  };

  const yAxisMax =
    getYAxisMax(maxValue);

  /*
   * Generate INTEGER ticks only.
   *
   * This prevents values such as:
   * 0.5
   * 1.5
   * 2.5
   *
   * from appearing on the Y-axis.
   */
  const getIntegerTicks = (
    max: number
  ): number[] => {
    if (max <= 5) {
      return Array.from(
        { length: max + 1 },
        (_, index) => index
      );
    }

    const step =
      max <= 20
        ? 5
        : 10;

    const ticks: number[] = [];

    for (
      let value = 0;
      value <= max;
      value += step
    ) {
      ticks.push(value);
    }

    /*
     * Make sure maximum is included.
     */
    if (
      ticks[ticks.length - 1] !== max
    ) {
      ticks.push(max);
    }

    return ticks;
  };

  const yAxisTicks =
    getIntegerTicks(yAxisMax);

  return (
    <div className="rounded-[18px] border border-[#D9DDE3] bg-white p-4">

      {/* Header */}
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-[14px] font-semibold text-[#003E7E]">
          Productivity Distribution
        </h3>

        <span className="rounded-full bg-[#EAF3FF] px-3 py-1 text-[10px] text-[#2563EB]">
          Associates by productivity bucket
        </span>
      </div>

      {/* Chart */}
      <div className="h-[250px]">
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <BarChart
            data={chartData}
            margin={{
              top: 15,
              right: 10,
              left: 10,
              bottom: 5,
            }}
          >

            <CartesianGrid
              vertical={false}
              stroke="#ECEFF3"
            />

            <XAxis
              dataKey="range"
              tick={{
                fontSize: 11,
                fill: '#475569',
              }}
              tickLine={false}
              axisLine={false}
            />

            <YAxis
              type="number"

              /*
               * IMPORTANT:
               * Always start Y-axis from 0.
               */
              domain={[
                0,
                yAxisMax,
              ]}

              /*
               * Explicit integer ticks.
               */
              ticks={yAxisTicks}

              allowDecimals={false}

              tick={{
                fontSize: 11,
                fill: '#475569',
              }}

              tickLine={false}
              axisLine={false}

              label={{
                value:
                  'No. of Associates',
                angle: -90,
                position: 'center',
                dx: -30,
                style: {
                  fill: '#1E293B',
                  fontSize: 12,
                  fontWeight: 600,
                  textAnchor:
                    'middle',
                },
              }}
            />

            <Tooltip
              cursor={{
                fill: '#F8FAFC',
              }}
              content={
                <CustomTooltip />
              }
            />

            <Bar
              dataKey="value"
              radius={[
                4,
                4,
                0,
                0,
              ]}
              barSize={40}
            >
              {chartData.map(
                (item) => (
                  <Cell
                    key={item.range}
                    fill={item.color}
                  />
                )
              )}

              <LabelList
                dataKey="value"
                position="center"
                fill="#FFFFFF"
                fontSize={12}
                fontWeight={700}
              />
            </Bar>

          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Footer */}
      <div className="mt-1 text-center text-[11px] font-medium text-slate-600">
        Productivity Range
      </div>

    </div>
  );
};

export default ProductivityDistributionChart;